import type { CookieConsentValue } from "./consent";

export type GoogleConsentState = "granted" | "denied";

export type GoogleConsentSettings = {
  ad_storage: GoogleConsentState;
  ad_user_data: GoogleConsentState;
  ad_personalization: GoogleConsentState;
  analytics_storage: GoogleConsentState;
  wait_for_update?: number;
};

export const GOOGLE_CONSENT_DENIED: GoogleConsentSettings = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
};

export const GOOGLE_CONSENT_GRANTED: GoogleConsentSettings = {
  ad_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
  analytics_storage: "granted",
};

/** Analytics on, every ad signal off: GPC or "Do not sell or share". */
export const GOOGLE_CONSENT_ANALYTICS_ONLY: GoogleConsentSettings = {
  ...GOOGLE_CONSENT_DENIED,
  analytics_storage: "granted",
};

/** Consent Mode settings that match a cookie choice. No choice means denied. */
export const googleConsentSettingsFor = (
  consent: CookieConsentValue | null,
): GoogleConsentSettings => {
  if (consent === "all") return GOOGLE_CONSENT_GRANTED;
  if (consent === "analytics") return GOOGLE_CONSENT_ANALYTICS_ONLY;
  return GOOGLE_CONSENT_DENIED;
};

/**
 * Push a gtag consent command the way Google expects (`dataLayer.push(arguments)`).
 * Object-literal pushes are not reliably parsed as Consent Mode commands.
 */
export const pushGoogleConsentCommand = (
  command: "default" | "update",
  settings: GoogleConsentSettings,
): void => {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];

  // Must be a classic function so `arguments` is available for gtag/GTM.
  function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params -- required by Google consent API
    window.dataLayer!.push(arguments);
  }

  gtag("consent", command, settings);
};

/**
 * With ads denied (Essential only, "Do not sell or share", or GPC), tell Google
 * tags to restrict data processing (CCPA) and redact ad click identifiers.
 */
export const pushGoogleAdsRestrictions = (): void => {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];

  function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params -- required by Google tag API
    window.dataLayer!.push(arguments);
  }

  gtag("set", "restricted_data_processing", true);
  gtag("set", "ads_data_redaction", true);
};
